import { useState } from 'react';
import {
  AppBar as MuiAppBar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  List,
  ListItem,
  ListItemAvatar,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import { Menu as MenuIcon, Person2Rounded, Logout, Monitor } from '@mui/icons-material';
import { deleteSession, getUserSession } from 'utils/userUtils';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumbs from 'components/Breadcrumbs';
import TaskBar from 'components/TaskBar';

function AppBar(props) {
  const navigate = useNavigate();
  const { t } = useTranslation('Actions');
  const [anchorEl, setAnchorEl] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const session = getUserSession();

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const loggout = () => {
    handleCloseMenu();
    deleteSession();
    navigate('/login');
  };

  return (
    <MuiAppBar
      elevation={0}
      position='fixed'
      sx={{
        position: 'fixed',
        height: '48px',
        flexShrink: 0,
        background: '#FFFFFF',
        color: '#343A40',
        width: { sm: 'calc(100% - var(--sidebar-width, 248px))' },
        // Barra laranja inferior (2px) sobreposta a todos os elementos.
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '46px',
          left: 0,
          right: 0,
          height: '2px',
          background: '#FF3D03',
          zIndex: 10,
        },
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '48px' }}>
        {/* Botão de menu (mobile) */}
        <IconButton
          color='inherit'
          aria-label='open drawer'
          edge='start'
          onClick={props.handleDrawerToggle}
          sx={{
            display: { sm: 'none' },
            position: 'absolute',
            left: 4,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <MenuIcon sx={{ color: '#343A40' }} />
        </IconButton>

        {/* Breadcrumb (esquerda) */}
        <Box
          sx={{
            position: 'absolute',
            left: { xs: 56, sm: 16 },
            top: 0,
            height: '48px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Breadcrumbs />
        </Box>

        {/* Área de Infos (saudação + tarefas + ícone de resolução) */}
        <Box
          role='complementary'
          aria-label='Área de informações'
          sx={{
            position: 'absolute',
            right: '80px',
            top: 0,
            height: '48px',
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            justifyContent: 'end',
            gap: '24px',
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Open Sans', sans-serif",
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: 'normal',
              color: '#343A40',
              textAlign: 'right',
              whiteSpace: 'nowrap',
            }}
          >
            Olá, {session?.nome}
          </Typography>

          <TaskBar />

          {/* Ícone de Resolução + tooltip */}
          <Box
            sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <IconButton
              disableRipple
              aria-label='Ajuda com resolução de tela'
              aria-describedby='resolution-tooltip'
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setShowTooltip(false)}
              sx={{
                p: 0,
                color: '#343A40',
                '&:focus-visible': {
                  outline: '2px solid #FF3D03',
                  outlineOffset: '2px',
                  borderRadius: '4px',
                },
              }}
            >
              <Monitor sx={{ width: 28, height: 28, color: '#343A40' }} />
            </IconButton>

            {showTooltip && (
              <Box
                id='resolution-tooltip'
                role='tooltip'
                sx={{
                  position: 'absolute',
                  top: '36px',
                  right: 0,
                  zIndex: 50,
                  width: '200px',
                  minHeight: '88px',
                  p: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#FFFFFF',
                  borderRadius: '4px',
                  boxShadow: 3,
                  '@keyframes fade-in-down': {
                    from: { opacity: 0, transform: 'translateY(-10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  animation: 'fade-in-down 0.2s ease-out forwards',
                }}
              >
                <Typography
                  component='p'
                  sx={{
                    fontFamily: "'Open Sans', sans-serif",
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: 1.4,
                    color: '#999999',
                    textAlign: 'center',
                    '& b': { fontWeight: 700, color: '#4D4D4D' },
                  }}
                >
                  Para aumentar ou diminuir a tela, pressione <b>Ctrl +</b> ou{' '}
                  <b>Ctrl -</b> no seu teclado. Para voltar ao tamanho original,
                  pressione <b>Ctrl + 0</b>.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Área do Usuário (extrema direita, colada na borda) */}
        <Box
          role='complementary'
          aria-label='Área do usuário'
          onClick={handleOpenMenu}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '64px',
            height: '48px',
            background: '#343A40',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Person2Rounded sx={{ width: 22, height: 22, color: '#FFFFFF' }} />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <List disablePadding>
          <ListItem>
            <ListItemAvatar>
              <Avatar>
                <Person2Rounded />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={session?.nome}
              secondary={session?.email}
            />
          </ListItem>
        </List>
        <Divider />
        <MenuItem onClick={loggout}>
          <ListItemIcon>
            <Logout fontSize='small' />
          </ListItemIcon>
          <ListItemText>{t('Sair')}</ListItemText>
        </MenuItem>
      </Menu>
    </MuiAppBar>
  );
}
export default AppBar;
